/** How a due date should read next to a task. Overdue and future dates get a
 *  weekday only while a weekday is still unambiguous (six days either side);
 *  anything older or further gets a real date — "Carried from Tuesday" on a
 *  39-day-old task was the bug this replaces. */
export type DueLabel =
  | { kind: "today" }
  | { kind: "carried_weekday" | "carried_since" | "future_weekday" | "future_date"; date: Date };

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_WINDOW_DAYS = 6;

const startOfLocalDay = (ts: number): number => {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export function dueLabelOf(due: number, now: number): DueLabel {
  // Whole local days between the two calendar dates; Math.round absorbs DST's 23/25-hour days.
  const diffDays = Math.round((startOfLocalDay(due) - startOfLocalDay(now)) / DAY_MS);
  const date = new Date(due);
  if (diffDays === 0) return { kind: "today" };
  if (diffDays < 0) {
    return diffDays >= -WEEKDAY_WINDOW_DAYS ? { kind: "carried_weekday", date } : { kind: "carried_since", date };
  }
  return diffDays <= WEEKDAY_WINDOW_DAYS ? { kind: "future_weekday", date } : { kind: "future_date", date };
}

/** The date part of a label — a weekday name for the near window, "Jul 28" beyond it. */
export function dueDateText(label: DueLabel, lang: string, weekday: "long" | "short" = "long"): string {
  switch (label.kind) {
    case "carried_weekday":
    case "future_weekday":
      return label.date.toLocaleDateString(lang, { weekday });
    case "carried_since":
    case "future_date":
      return label.date.toLocaleDateString(lang, { month: "short", day: "numeric" });
    default:
      return "";
  }
}
